(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Cart count + toast ---------- */

  function setCartCount(count) {
    document.querySelectorAll('[data-dz-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-dz-cart-count-text]').forEach((el) => {
      el.textContent = String(count);
    });
  }

  let toastTimer;
  function showToast(message, linkLabel, linkHref) {
    let toast = document.querySelector('.dz-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dz-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = '';
    const text = document.createElement('span');
    text.textContent = message;
    toast.appendChild(text);
    if (linkLabel && linkHref) {
      const link = document.createElement('a');
      link.href = linkHref;
      link.textContent = linkLabel;
      toast.appendChild(link);
    }
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 5000);
  }

  /* ---------- Quantity steppers ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-dz-step]') : null;
    if (!button) return;
    const wrap = button.closest('[data-dz-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 0);
    const max = Number(input.max || 99);
    const current = Number(input.value) || min;
    const next = Math.min(max, Math.max(min, current + Number(button.dataset.dzStep)));
    if (next === current) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Mobile menu ---------- */

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-dz-menu-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener(
          'close',
          () => {
            opener.setAttribute('aria-expanded', 'false');
            opener.focus();
          },
          { once: true }
        );
      }
      return;
    }

    const drawer = target.closest('[data-dz-drawer]');
    if (target.closest('[data-dz-menu-close]') && drawer) {
      closeDrawer(drawer);
      return;
    }
    if (target.matches('[data-dz-drawer]')) {
      closeDrawer(target);
    }
  });

  /** Plays the slide-out before closing when animations are on. */
  function closeDrawer(drawer) {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;
    if (!document.documentElement.classList.contains('dz-motion')) {
      drawer.close();
      return;
    }
    drawer.classList.add('is-closing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      drawer.removeEventListener('animationend', onEnd);
      drawer.classList.remove('is-closing');
      drawer.close();
    };
    const onEnd = (event) => {
      if (event.target === drawer) finish();
    };
    drawer.addEventListener('animationend', onEnd);
    window.setTimeout(finish, 450);
  }

  // Escape key: animate out too.
  document.addEventListener(
    'cancel',
    (event) => {
      const drawer = event.target;
      if (drawer instanceof HTMLDialogElement && drawer.matches('[data-dz-drawer]')) {
        event.preventDefault();
        closeDrawer(drawer);
      }
    },
    true
  );

  /* ---------- Product ---------- */

  class DzProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-dz-form]');
      this.viewport = this.querySelector('[data-dz-viewport]');
      this.sticky = this.querySelector('[data-dz-sticky]');
      this.errorEl = this.querySelector('[data-dz-error]');
      try {
        this.variants = JSON.parse(this.querySelector('[data-dz-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      this.busy = false;

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-dz-variant]')) this.onVariantChange();
      });
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-dz-thumb]');
        if (thumb) this.showMedia(thumb.dataset.dzThumb, true);
      });
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.setupGallery();
      this.setupSticky();
    }

    get variant() {
      const checked = this.querySelector('[data-dz-variant]:checked');
      const id = checked ? Number(checked.value) : null;
      return this.variants.find((v) => v.id === id) || null;
    }

    onVariantChange() {
      const variant = this.variant;
      if (!variant) return;
      const label = variant.available ? 'Add to bag' : 'Sold out';

      this.querySelectorAll('[data-dz-price]').forEach((el) => (el.textContent = variant.price));
      this.querySelectorAll('[data-dz-add]').forEach((btn) => {
        btn.disabled = !variant.available;
        const inner = btn.querySelector('[data-dz-add-label]');
        (inner || btn).textContent = label;
      });
      this.querySelectorAll('[data-dz-buy]').forEach((btn) => (btn.disabled = !variant.available));
      const stickyTitle = this.querySelector('[data-dz-sticky-title]');
      const stickyPrice = this.querySelector('[data-dz-sticky-price]');
      if (stickyTitle) stickyTitle.textContent = variant.title;
      if (stickyPrice) stickyPrice.textContent = variant.price;

      if (variant.mediaId) this.showMedia(String(variant.mediaId), false);
      this.hideError();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url.toString());
    }

    setupGallery() {
      const viewport = this.viewport;
      if (!viewport) return;
      const slides = Array.from(viewport.querySelectorAll('[data-media-id]'));
      if (slides.length < 2) return;

      let frame;
      viewport.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const index = Math.round(viewport.scrollLeft / viewport.clientWidth);
            const slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
            if (slide) this.markMedia(slide.dataset.mediaId);
          });
        },
        { passive: true }
      );

      const initial = this.variant && this.variant.mediaId;
      if (initial) this.showMedia(String(initial), false, true);
      else this.markMedia(slides[0].dataset.mediaId);
    }

    showMedia(mediaId, focus, instant) {
      if (!this.viewport || !mediaId) return;
      const slide = this.viewport.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      this.viewport.scrollTo({
        left: slide.offsetLeft - this.viewport.offsetLeft,
        behavior: instant || reduceMotion() ? 'auto' : 'smooth',
      });
      this.markMedia(mediaId);
      if (focus) this.viewport.focus({ preventScroll: true });
    }

    markMedia(mediaId) {
      this.querySelectorAll('[data-dz-thumb]').forEach((thumb) => {
        if (thumb.dataset.dzThumb === String(mediaId)) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.querySelectorAll('[data-dz-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot.dataset.dzDot === String(mediaId));
      });
    }

    setupSticky() {
      const sticky = this.sticky;
      const anchor = this.querySelector('[data-dz-actions]');
      if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.toggleAttribute('inert', !show);
        sticky.setAttribute('aria-hidden', String(!show));
      });
      observer.observe(anchor);
    }

    async onSubmit(event) {
      const submitter = event.submitter;
      if (submitter && submitter.hasAttribute('data-dz-buy')) return;
      if (!window.fetch) return;
      event.preventDefault();
      if (this.busy) return;

      const variant = this.variant;
      if (!variant || !variant.available) return;

      this.busy = true;
      this.hideError();
      const buttons = Array.from(this.querySelectorAll('[data-dz-add]'));
      buttons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));

      const formData = new FormData(this.form);
      formData.delete('return_to');
      formData.delete('add');

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || 'We could not add this pack. Please try again.');
          return;
        }
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        setCartCount(cart.item_count);
        showToast(`${variant.title} added to your bag.`, 'View bag', `${root}cart`);
      } catch (error) {
        this.showError('We could not reach the store. Check your connection and try again.');
      } finally {
        this.busy = false;
        buttons.forEach((btn) => btn.removeAttribute('aria-busy'));
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  if (!customElements.get('dz-product')) customElements.define('dz-product', DzProduct);

  /* ---------- Cart page ---------- */

  async function changeLine(section, line, quantity) {
    const form = section.querySelector('[data-dz-cart-form]');
    if (form) form.classList.add('is-loading');
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: [section.dataset.sectionId] }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'We could not update your bag.');
      }
      const html = data.sections && data.sections[section.dataset.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-dz-cart]');
        if (fresh) section.innerHTML = fresh.innerHTML;
      }
      setCartCount(data.item_count);
    } catch (error) {
      if (form) form.classList.remove('is-loading');
      const errorEl = section.querySelector('[data-dz-error]');
      if (errorEl) {
        errorEl.textContent = error.message || 'We could not update your bag. Please try again.';
        errorEl.hidden = false;
      }
      const input = section.querySelector(`[data-dz-line-qty="${line}"]`);
      if (input) input.value = input.defaultValue;
    }
  }

  const qtyTimers = new Map();

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-dz-line-qty]')) return;
    const section = input.closest('[data-dz-cart]');
    if (!section) return;
    const line = Number(input.dataset.dzLineQty);
    clearTimeout(qtyTimers.get(line));
    qtyTimers.set(
      line,
      setTimeout(() => changeLine(section, line, Math.max(0, Number(input.value) || 0)), 350)
    );
  });

  document.addEventListener('click', (event) => {
    const remove = event.target instanceof Element ? event.target.closest('[data-dz-remove]') : null;
    if (!remove) return;
    const section = remove.closest('[data-dz-cart]');
    if (!section || !window.fetch) return;
    event.preventDefault();
    changeLine(section, Number(remove.dataset.dzRemove), 0);
  });

  let noteTimer;
  document.addEventListener('input', (event) => {
    const note = event.target;
    if (!(note instanceof HTMLTextAreaElement) || note.name !== 'note' || !note.closest('[data-dz-cart]')) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value }),
      }).catch(() => {});
    }, 500);
  });

  /* ---------- FAQ topic tabs ---------- */

  function showFaqTopic(section, topic) {
    section.querySelectorAll('[data-dz-faq-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.dzFaqTab === topic));
    });
    section.querySelectorAll('[data-dz-faq-category]').forEach((item) => {
      item.hidden = item.dataset.dzFaqCategory !== topic;
    });
  }

  function initFaqTabs(scope) {
    scope.querySelectorAll('[data-dz-faq]').forEach((section) => {
      // Start with every question visible and the first tab marked; a tab click filters.
      section.querySelectorAll('[data-dz-faq-tab]').forEach((tab, index) => {
        tab.setAttribute('aria-selected', String(index === 0));
      });
    });
  }

  document.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-dz-faq-tab]') : null;
    if (!tab) return;
    const section = tab.closest('[data-dz-faq]');
    if (section) showFaqTopic(section, tab.dataset.dzFaqTab);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFaqTabs(document));
  } else {
    initFaqTabs(document);
  }
  document.addEventListener('shopify:section:load', (event) => initFaqTabs(event.target));

  /* Keep the bag count right when returning with the back button. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => setCartCount(cart.item_count))
      .catch(() => {});
  });
})();
